import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Plus, Trash2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Page } from "@/components/Page";
import { AvatarUploader, MediaUploader } from "@/components/Uploader";

const empty = {
  profile_type: "Individual", firm_name: "", proprietor_name: "", gst_number: "", pan_number: "",
  msme_number: "", address: "", history: "", item_name: "", gi_item_name: "", gi_application_no: "",
  facebook_url: "", profile_photo: "", achievements: [],
};

export default function Profile() {
  const [f, setF] = useState(empty);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then(r => { if (r.data.profile) setF({ ...empty, ...r.data.profile, achievements: r.data.profile.achievements || [] }); }).catch(() => {});
  }, []);

  const change = (e) => { setF({ ...f, [e.target.name]: e.target.value }); setSaved(false); };
  const setPhoto = (p) => { setF({ ...f, profile_photo: p }); setSaved(false); };
  const setAch = (idx, patch) => {
    setF({ ...f, achievements: f.achievements.map((a, i) => i === idx ? { ...a, ...patch } : a) });
    setSaved(false);
  };
  const addAch = () => setF({ ...f, achievements: [...f.achievements, { title: "", description: "", date: "", photos: [], videos: [] }] });
  const rmAch = (idx) => setF({ ...f, achievements: f.achievements.filter((_, i) => i !== idx) });

  const save = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      // achievements: photos/videos are stored as [{path,kind},...] in state — flatten to path arrays for API
      const payload = {
        ...f,
        achievements: f.achievements.map(a => ({
          title: a.title, description: a.description, date: a.date,
          photos: (a.photos || []).map(m => m.path || m).filter(Boolean),
          videos: (a.videos || []).map(m => m.path || m).filter(Boolean),
        })),
      };
      await api.put("/profile", payload);
      setSaved(true);
      toast.success("Profile saved");
    } catch (err) {
      const m = formatError(err); setError(m); toast.error(m);
    } finally { setBusy(false); }
  };

  const fields = [
    ["firm_name", "Firm / individual name"],
    ["proprietor_name", "Proprietor name"],
    ["gst_number", "GST number"],
    ["pan_number", "PAN number"],
    ["msme_number", "MSME number"],
    ["gi_application_no", "GI application number"],
    ["item_name", "Your product name"],
    ["gi_item_name", "Related GI item name"],
    ["facebook_url", "Facebook page URL"],
  ];

  const toMediaList = (paths = [], kind = "image") => paths.map(p => (typeof p === "string" ? { path: p, kind } : p));

  return (
    <Page title="Your GI profile" subtitle="A verified profile helps buyers understand the people and place behind your product.">
      <form onSubmit={save} className="form-section">
        <div className="form-top">
          <div><p className="eyebrow">PUBLIC PRESENCE</p><h3>Tell your story</h3></div>
          <span className="status-pill"><span /> {saved ? "Saved just now" : "Draft profile"}</span>
        </div>
        {error && <div className="error" data-testid="profile-error">{error}</div>}

        <AvatarUploader value={f.profile_photo} onChange={setPhoto} testId="profile-photo" />

        <div className="field-grid">
          {fields.map(([name, label]) => (
            <label key={name}>
              {label}
              <input data-testid={`profile-${name}-input`} name={name} value={f[name]} onChange={change} placeholder={`Enter ${label.toLowerCase()}`} />
            </label>
          ))}
        </div>
        <label>Complete address<textarea data-testid="profile-address-input" name="address" value={f.address} onChange={change} placeholder="Street, village, district, state, PIN" /></label>
        <label>Family and craft history<textarea data-testid="profile-history-input" name="history" value={f.history} onChange={change} placeholder="From your family history to the work you do today..." /></label>

        <div style={{ marginTop: 20 }}>
          <div className="form-top">
            <div><p className="eyebrow">RECOGNITION</p><h3>Achievements & awards</h3></div>
            <button type="button" className="secondary-btn" onClick={addAch} data-testid="add-achievement-button"><Plus size={14} /> Add achievement</button>
          </div>

          {f.achievements.length === 0 && <p className="muted" style={{ fontSize: 13 }}>Add awards, recognitions, or milestones — buyers love to see them.</p>}

          {f.achievements.map((a, i) => (
            <div className="card-inline" key={i} data-testid={`achievement-${i}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <h4>Achievement #{i + 1}</h4>
                <button type="button" className="danger-btn" onClick={() => rmAch(i)} data-testid={`achievement-${i}-remove`}><Trash2 size={12} /> Remove</button>
              </div>
              <div className="field-grid">
                <label>Title<input value={a.title} onChange={e => setAch(i, { title: e.target.value })} placeholder="e.g., National Handicrafts Award" data-testid={`achievement-${i}-title`} /></label>
                <label>Date<input type="date" value={a.date} onChange={e => setAch(i, { date: e.target.value })} data-testid={`achievement-${i}-date`} /></label>
              </div>
              <label>Description<textarea value={a.description} onChange={e => setAch(i, { description: e.target.value })} placeholder="A short story about this recognition" data-testid={`achievement-${i}-desc`} /></label>
              <MediaUploader
                label="Achievement photos"
                value={toMediaList(a.photos, "image")}
                onChange={list => setAch(i, { photos: list })}
                accept="image/*"
                max={6}
                testIdPrefix={`achievement-${i}-photos`}
              />
              <MediaUploader
                label="Achievement videos"
                value={toMediaList(a.videos, "video")}
                onChange={list => setAch(i, { videos: list })}
                accept="video/*"
                max={2}
                testIdPrefix={`achievement-${i}-videos`}
              />
            </div>
          ))}
        </div>

        <div className="form-actions">
          <span className="muted">You can update these details anytime.</span>
          <button className="primary-btn" data-testid="profile-save-button" disabled={busy}>
            {busy ? "Saving…" : "Save profile"} <ArrowUpRight size={17} />
          </button>
        </div>
      </form>
    </Page>
  );
}
